import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import TopBarRedesigned from 'components/TopBarRedesigned/TopBarRedesigned';
import Api from 'api';
import { getSoftwareOptions } from 'utils/dentalSoftware';
import { Box, Button, Container, Divider, Paper, Stack, MenuItem, TextField, Typography } from '@mui/material';
import { usePersistedState } from 'hooks';
import { entities } from 'reduxStore';

type Step = 1 | 2;

interface Step1FormState {
  secondaryEmail: string;
  accountingEmail: string;
  practiceName: string;
  businessOwner: string;
  address: string;
  city: string;
  province: string;
  postalCode: string;
  phone: string;
  email: string;
}

interface Step2FormState {
  primaryContact: string;
  parking: string;
  radiography: string;
  ultrasonic: string;
  avgApptTime: string;
  charting: string;
  software: string;
}

const provincesCA = ['AB','BC','MB','NB','NL','NS','ON','PE','QC','SK'];
const statesUS = ['AZ','CA','CO','CT','FL','ID','IL','LA','MA','MI','MN','NC','NY','OH','PA','TN','TX'];

interface AutocompleteProps {
  buildingNumber?: string;
  state: string;
  city?: string;
  postalCode?: string;
  address?: string;
  valueEqual?: boolean;
}

const LocationAddPage: React.FC = () => {
  const history = useHistory();
  const [user] = usePersistedState({ entityName: entities.user });
  const countryCode: number = (user as any)?.dentist?.country ?? (user as any)?.country ?? 0; // 0=CA,1=US
  const isUS = countryCode === 1;
  const softwareOptions = useMemo(() => getSoftwareOptions(countryCode), [countryCode]);

  const [step, setStep] = useState<Step>(1);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [autocompletedAddress, setAutocompletedAddress] = useState<AutocompleteProps | undefined>();

  const [f1, setF1] = useState<Step1FormState>({
    secondaryEmail: '',
    accountingEmail: '',
    practiceName: (user as any)?.dentist?.practiceName || '',
    businessOwner: '',
    address: '',
    city: (user as any)?.dentist?.city || '',
    province: '',
    postalCode: '',
    phone: '',
    email: (user as any)?.email || '',
  });

  const [f2, setF2] = useState<Step2FormState>({
    primaryContact: '',
    parking: '',
    radiography: '',
    ultrasonic: '',
    avgApptTime: '',
    charting: '',
    software: '',
  });

  const regionOptions = useMemo(() => (isUS ? statesUS : provincesCA), [isUS]);
  const regionLabel = isUS ? 'State' : 'Province';
  const postalLabel = isUS ? 'Zip Code' : 'Postal Code';

  const goBack = useCallback(() => {
    history.push({ pathname: 'dentistLocations' });
  }, [history]);

  // Google Places Autocomplete bridge (parity with SignUp1_DO)
  useEffect(() => {
    try {
      const ts: any = (window as any).TempStars;
      ts?.Autocomplete?.init?.({ country: countryCode }, (res: AutocompleteProps) => {
        const { state, city, postalCode, address, buildingNumber, valueEqual } = res || {} as AutocompleteProps;
        if (!address && !buildingNumber && !city) {
          setAutocompletedAddress(undefined);
        } else if (valueEqual) {
          setAutocompletedAddress({ state, city, postalCode, address, buildingNumber, valueEqual });
          setF1((prev) => ({
            ...prev,
            address: address || prev.address,
            city: city || prev.city,
            province: state || prev.province,
            postalCode: postalCode || prev.postalCode,
          }));
        }
      });
      return () => { try { ts?.Autocomplete?.removeListener?.(); } catch {} };
    } catch {}
  }, [countryCode]);

  const validateStep1 = useCallback((): string | null => {
    if (!f1.practiceName.trim()) return 'Practice Name is required.';
    if (!f1.address.trim() && !autocompletedAddress) return 'Please enter your address';
    if (f1.address.trim() && !autocompletedAddress) return 'Please select your address from the list';
    if (autocompletedAddress && !autocompletedAddress.buildingNumber?.length) return 'Add your building #, street';
    // Prefer autocompleted fields when present
    const cityVal = autocompletedAddress?.city ?? f1.city;
    const provVal = autocompletedAddress?.state ?? f1.province;
    const pcVal = autocompletedAddress?.postalCode ?? f1.postalCode;
    if (!cityVal?.trim()) return 'City is required.';
    if (!provVal?.trim()) return `${regionLabel} is required.`;
    if (!pcVal?.trim()) return `${postalLabel} is required.`;
    if (!f1.email.trim()) return 'Notifications Email is required.';
    if (!f1.phone.trim()) return 'Office Phone is required.';
    if (!f1.businessOwner.trim()) return 'Business Owner is required.';
    return null;
  }, [f1, autocompletedAddress, regionLabel, postalLabel]);

  const validateStep2 = useCallback((): string | null => {
    if (!f2.primaryContact.trim()) return 'Primary Contact is required.';
    if (!f2.parking) return 'Parking is required.';
    if (!f2.radiography) return 'Radiography type is required.';
    if (!f2.ultrasonic) return 'Ultrasonic type is required.';
    if (!f2.avgApptTime) return 'Avg Recall time is required.';
    if (!f2.charting) return 'Charting system is required.';
    if (!f2.software) return 'Dental software is required.';
    return null;
  }, [f2]);

  const goToStep2 = useCallback(() => {
    const validationError = validateStep1();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    setStep(2);
  }, [validateStep1]);

  const onSubmitStep2 = useCallback(async () => {
    const step1Error = validateStep1();
    if (step1Error) {
      setError(step1Error);
      setStep(1);
      return;
    }
    const step2Error = validateStep2();
    if (step2Error) {
      setError(step2Error);
      return;
    }
    setError('');
    setIsSubmitting(true);
    try {
      const resolvedAddress = (autocompletedAddress?.address || f1.address).trim();
      const resolvedCity = (autocompletedAddress?.city || f1.city).trim();
      const resolvedProvince = (autocompletedAddress?.state || f1.province).trim();
      const resolvedPostal = (autocompletedAddress?.postalCode || f1.postalCode).trim();
      const allowedSoftware = getSoftwareOptions(countryCode);
      const safeSoftware = f2.software && !allowedSoftware.includes(f2.software) ? 'Other' : f2.software;

      await Api.addLocationV2({
        address: resolvedAddress,
        city: resolvedCity,
        province: resolvedProvince,
        postalCode: resolvedPostal,
        email: f1.email.trim(),
        practiceName: f1.practiceName.trim(),
        country: countryCode,
        phone: f1.phone.trim(),
        businessOwner: f1.businessOwner.trim(),
        secondaryEmail: f1.secondaryEmail.trim() || undefined,
        accountingEmail: f1.accountingEmail.trim() || undefined,
        primaryContact: f2.primaryContact.trim(),
        parking: f2.parking,
        radiography: f2.radiography,
        ultrasonic: f2.ultrasonic,
        avgApptTime: f2.avgApptTime,
        charting: f2.charting,
        software: safeSoftware,
        isUpdated: 1,
      });
      try {
        await (window as any).TempStars?.User?.refresh?.();
      } catch {
        /* non-fatal */
      }
      history.push({ pathname: 'dentistLocations' });
    } catch (e: any) {
      setError(e?.message || 'Error adding location.');
    } finally {
      setIsSubmitting(false);
    }
  }, [autocompletedAddress, countryCode, f1, f2, history, validateStep1, validateStep2]);

  const formActionButtonSx = {
    minHeight: 44,
    py: 1,
    fontSize: '0.875rem',
    textTransform: 'none' as const,
    width: { xs: '100%', sm: 'auto' },
    flex: { sm: 1 },
  };

  const formFieldRowSx = {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    gap: 2,
    width: '100%',
    '& > *': { flex: { sm: 1 }, minWidth: 0, width: '100%' },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <TopBarRedesigned onBack={goBack} title={'Add Location'} useStandardBack />
      <Container maxWidth="md" sx={{ flex: 1, overflow: 'auto', py: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 }, width: '100%', boxSizing: 'border-box' }}>
        {error && (
          <Paper elevation={0} sx={{ mb: 2, p: 2, borderRadius: 2, border: '1px solid var(--divider, #E0E0E0)' }}>
            <Typography color="error" variant="body2">{error}</Typography>
          </Paper>
        )}

        {step === 1 && (
          <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, border: '1px solid var(--divider, #E0E0E0)', bgcolor: 'var(--common-white-main, #fff)', width: '100%', boxSizing: 'border-box', overflow: 'visible' }}>
            <Stack spacing={2} sx={{ width: '100%' }}>
              <Box>
                <Typography variant="overline" color="text.secondary">Step 1 of 2</Typography>
                <Typography variant="h6">New Location Details</Typography>
              </Box>
              <Divider />

              <Stack spacing={2} sx={{ width: '100%' }}>
                <TextField
                  fullWidth
                  required
                  label="Start typing your address ..."
                  id={'autocomplete'}
                  value={f1.address}
                  onChange={(e) => setF1({ ...f1, address: e.target.value })}
                  helperText={autocompletedAddress && !autocompletedAddress.buildingNumber ? 'Add your building #, street' : undefined}
                />

                <Box sx={formFieldRowSx}>
                  <TextField fullWidth required label="City" value={f1.city} onChange={(e) => setF1({ ...f1, city: e.target.value })} />
                  <TextField fullWidth select required label={regionLabel} value={f1.province} onChange={(e) => setF1({ ...f1, province: e.target.value })}>
                    <MenuItem value="">Select {regionLabel}</MenuItem>
                    {regionOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </TextField>
                  <TextField fullWidth required label={postalLabel} value={f1.postalCode} onChange={(e) => setF1({ ...f1, postalCode: e.target.value })} />
                </Box>

                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="text.secondary">Contact & Notifications</Typography>

                <Box sx={formFieldRowSx}>
                  <TextField fullWidth required label="Notifications Email" value={f1.email} onChange={(e) => setF1({ ...f1, email: e.target.value })} />
                  <TextField fullWidth required label="Office Phone" value={f1.phone} onChange={(e) => setF1({ ...f1, phone: e.target.value })} />
                </Box>
                <Box sx={formFieldRowSx}>
                  <TextField fullWidth label="Secondary Notifications Email" value={f1.secondaryEmail} onChange={(e) => setF1({ ...f1, secondaryEmail: e.target.value })} />
                  <TextField fullWidth label="Accounting Dept. Email" value={f1.accountingEmail} onChange={(e) => setF1({ ...f1, accountingEmail: e.target.value })} />
                </Box>

                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="text.secondary">Practice Info</Typography>

                <Box sx={formFieldRowSx}>
                  <TextField fullWidth required label="Practice Name" value={f1.practiceName} onChange={(e) => setF1({ ...f1, practiceName: e.target.value })} />
                  <TextField fullWidth required label="Business Owner" value={f1.businessOwner} onChange={(e) => setF1({ ...f1, businessOwner: e.target.value })} />
                </Box>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1, width: '100%' }}>
                <Button fullWidth variant="outlined" size="medium" sx={formActionButtonSx} onClick={goBack}>Cancel</Button>
                <Button fullWidth variant="contained" size="medium" sx={formActionButtonSx} onClick={goToStep2}>
                  Next
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}

        {step === 2 && (
          <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, border: '1px solid var(--divider, #E0E0E0)', bgcolor: 'var(--common-white-main, #fff)', mt: { xs: 2, sm: 3 }, width: '100%', boxSizing: 'border-box', overflow: 'visible' }}>
            <Stack spacing={2} sx={{ width: '100%' }}>
              <Box>
                <Typography variant="overline" color="text.secondary">Step 2 of 2</Typography>
                <Typography variant="h6">Practice Details</Typography>
              </Box>
              <Divider />

              <Stack spacing={2} sx={{ width: '100%' }}>
                <Box sx={formFieldRowSx}>
                  <TextField fullWidth required label="Primary Contact" value={f2.primaryContact} onChange={(e) => setF2({ ...f2, primaryContact: e.target.value })} />
                  <TextField fullWidth required select label="Parking" value={f2.parking} onChange={(e) => setF2({ ...f2, parking: e.target.value })}>
                    <MenuItem value="">Parking is:</MenuItem>
                    <MenuItem value="free-on-site">Free on-site</MenuItem>
                    <MenuItem value="temp-pays">Temp pays for parking</MenuItem>
                    <MenuItem value="street">Street</MenuItem>
                    <MenuItem value="public-lot">Pay Parking @ Public Lot</MenuItem>
                    <MenuItem value="no-parking">No parking available</MenuItem>
                  </TextField>
                </Box>
                <Box sx={formFieldRowSx}>
                  <TextField fullWidth required select label="Radiography" value={f2.radiography} onChange={(e) => setF2({ ...f2, radiography: e.target.value })}>
                    <MenuItem value="">Type of radiographs</MenuItem>
                    <MenuItem value="film">Film</MenuItem>
                    <MenuItem value="digital">Digital sensors</MenuItem>
                    <MenuItem value="phosphor-plates">Phosphor plates</MenuItem>
                  </TextField>
                  <TextField fullWidth required select label="Ultrasonic" value={f2.ultrasonic} onChange={(e) => setF2({ ...f2, ultrasonic: e.target.value })}>
                    <MenuItem value="">Piezo or Cavitron?</MenuItem>
                    <MenuItem value="cavitron">Cavitron</MenuItem>
                    <MenuItem value="piezo">Piezo</MenuItem>
                    <MenuItem value="both">Both</MenuItem>
                    <MenuItem value="none">None</MenuItem>
                  </TextField>
                </Box>
                <Box sx={formFieldRowSx}>
                  <TextField fullWidth required select label="Avg Recall" value={f2.avgApptTime} onChange={(e) => setF2({ ...f2, avgApptTime: e.target.value })}>
                    <MenuItem value="">Adult recalls are:</MenuItem>
                    <MenuItem value="30min">30min</MenuItem>
                    <MenuItem value="40min">40min</MenuItem>
                    <MenuItem value="45min">45min</MenuItem>
                    <MenuItem value="50min">50min</MenuItem>
                    <MenuItem value="60min">60min</MenuItem>
                    <MenuItem value="70min">70min</MenuItem>
                  </TextField>
                  <TextField fullWidth required select label="Charting" value={f2.charting} onChange={(e) => setF2({ ...f2, charting: e.target.value })}>
                    <MenuItem value="">Charting system is:</MenuItem>
                    <MenuItem value="paper charts">Paper charts</MenuItem>
                    <MenuItem value="digital charts">Digital charts</MenuItem>
                  </TextField>
                </Box>
                <TextField fullWidth required select label="Software" value={f2.software} onChange={(e) => setF2({ ...f2, software: e.target.value })}>
                  <MenuItem value="">Dental software is:</MenuItem>
                  <MenuItem value="Dentrix">Dentrix</MenuItem>
                  <MenuItem value="AbelDent">AbelDent</MenuItem>
                  <MenuItem value="Tracker">Tracker</MenuItem>
                  <MenuItem value="EagleSoft">EagleSoft</MenuItem>
                  <MenuItem value="Paradigm">Paradigm</MenuItem>
                  <MenuItem value="Open Dental">Open Dental</MenuItem>
                  <MenuItem value="Curve Dental/Curve Hero">Curve Dental/Curve Hero</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1, width: '100%' }}>
                <Button fullWidth variant="outlined" size="medium" sx={formActionButtonSx} onClick={() => setStep(1)} disabled={isSubmitting}>Back</Button>
                <Button fullWidth variant="contained" size="medium" sx={formActionButtonSx} onClick={onSubmitStep2} disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Submit'}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}
      </Container>
    </div>
  );
};

export default LocationAddPage;


